// signatureFonts — save & reuse user-uploaded signature fonts.
//
// Privacy model: fonts are stored in the PRIVATE `signature-fonts` bucket,
// scoped to the uploader by `auth.uid()`. Workspace teammates do NOT have
// access. This sidesteps font-licensing redistribution concerns while still
// freeing the user from re-uploading the same font for each new signature.

import { supabase } from "@/integrations/supabase/client";
import { validateSignatureFontFile } from "@/lib/signatureRasterizer";

export const SIGNATURE_FONTS_BUCKET = "signature-fonts";
const SIGNED_URL_TTL_SECONDS = 60 * 60;

export type SignatureFont = {
  id: string;
  workspaceId: string;
  ownerUserId: string;
  displayName: string;
  originalFilename: string;
  storagePath: string;
  fileSize: number;
  contentType: string;
  licenseConfirmedAt: string | null;
  createdAt: string;
};

type Row = {
  id: string;
  workspace_id: string;
  owner_user_id: string;
  display_name: string;
  original_filename: string;
  storage_path: string;
  file_size: number;
  content_type: string;
  license_confirmed_at: string | null;
  created_at: string;
};

const rowToFont = (r: Row): SignatureFont => ({
  id: r.id,
  workspaceId: r.workspace_id,
  ownerUserId: r.owner_user_id,
  displayName: r.display_name,
  originalFilename: r.original_filename,
  storagePath: r.storage_path,
  fileSize: r.file_size,
  contentType: r.content_type,
  licenseConfirmedAt: r.license_confirmed_at,
  createdAt: r.created_at,
});

const extFromFilename = (name: string): string => {
  const lower = name.toLowerCase();
  for (const e of [".woff2", ".woff", ".otf", ".ttf"]) {
    if (lower.endsWith(e)) return e.slice(1);
  }
  return "bin";
};

const randomId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const listSignatureFonts = async (workspaceId: string): Promise<SignatureFont[]> => {
  const { data, error } = await supabase
    .from("composer_signature_fonts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => rowToFont(r as Row));
};

export const saveSignatureFont = async (params: {
  workspaceId: string;
  file: File;
  displayName: string;
  licenseConfirmed: boolean;
}): Promise<SignatureFont> => {
  const { workspaceId, file, displayName, licenseConfirmed } = params;
  const err = validateSignatureFontFile(file);
  if (err) throw new Error(err);

  const { data: userRes } = await supabase.auth.getUser();
  const userId = userRes.user?.id;
  if (!userId) throw new Error("Sign in to save signature fonts.");

  const ext = extFromFilename(file.name);
  const path = `${userId}/${randomId()}.${ext}`;
  const contentType = file.type || "application/octet-stream";

  const { error: upErr } = await supabase.storage
    .from(SIGNATURE_FONTS_BUCKET)
    .upload(path, file, { contentType, upsert: false });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from("composer_signature_fonts")
    .insert({
      workspace_id: workspaceId,
      owner_user_id: userId,
      display_name: displayName.trim() || file.name,
      original_filename: file.name,
      storage_path: path,
      file_size: file.size,
      content_type: contentType,
      license_confirmed_at: licenseConfirmed ? new Date().toISOString() : null,
    })
    .select("*")
    .single();

  if (error || !data) {
    // best-effort cleanup of uploaded file
    await supabase.storage.from(SIGNATURE_FONTS_BUCKET).remove([path]).catch(() => {});
    throw error ?? new Error("Failed to save signature font");
  }
  return rowToFont(data as Row);
};


export const deleteSignatureFont = async (font: SignatureFont): Promise<void> => {
  await supabase.storage.from(SIGNATURE_FONTS_BUCKET).remove([font.storagePath]).catch(() => {});
  const { error } = await supabase.from("composer_signature_fonts").delete().eq("id", font.id);
  if (error) throw error;
};

/** Fetch the stored font file and return it as a File for `loadSignatureFont`. */
export const fetchSignatureFontAsFile = async (font: SignatureFont): Promise<File> => {
  const { data, error } = await supabase.storage
    .from(SIGNATURE_FONTS_BUCKET)
    .createSignedUrl(font.storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) throw error ?? new Error("Could not load signature font.");
  const res = await fetch(data.signedUrl);
  if (!res.ok) throw new Error(`Could not download font (HTTP ${res.status}).`);
  const blob = await res.blob();
  return new File([blob], font.originalFilename, {
    type: font.contentType || "application/octet-stream",
  });
};
